import React, { useState } from 'react';
import { useSite } from '../../context/SiteContext';
import { SiteCommunication, CommunicationStatus } from '../../types';
import { EmptySiteNotice } from '../common/EmptySiteNotice';
import {
  Inbox,
  Mail,
  Phone,
  MessageSquare,
  Search,
  Filter,
  CheckCircle2,
  Clock,
  Archive,
  Trash2,
  ExternalLink,
  MessageCircle,
  Download,
  AlertCircle,
  Eye,
  RefreshCw,
  Send,
  User,
  Calendar,
  Sparkles,
} from 'lucide-react';

export const CommunicationsManager: React.FC<{ onNavigate?: (view: string) => void }> = ({ onNavigate }) => {
  const {
    activeSite,
    communications,
    unreadCommunicationsCount,
    loadCommunications,
    updateCommunicationStatus,
    deleteCommunication,
  } = useSite();

  const [statusFilter, setStatusFilter] = useState<'ALL' | CommunicationStatus>('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMessage, setSelectedMessage] = useState<SiteCommunication | null>(null);
  const [adminNoteInput, setAdminNoteInput] = useState('');
  const [isSavingNote, setIsSavingNote] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  if (!activeSite) {
    return (
      <EmptySiteNotice
        title="Gelen Mesaj Kutusu İçin Aktif Web Sitesi Gereklidir"
        description="Web siteniz üzerinden müşterilerinizin ilettiği iletişim formu ve WhatsApp taleplerini okumak için lütfen bir web sitesi oluşturun veya mevcut sitelerinizden birini aktif yapın."
        onOpenCreateSite={() => onNavigate && onNavigate('sites')}
        onReturnToDashboard={() => onNavigate && onNavigate('dashboard')}
      />
    );
  }

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await loadCommunications();
    setIsRefreshing(false);
  };

  const handleOpenDetail = (comm: SiteCommunication) => {
    setSelectedMessage(comm);
    setAdminNoteInput(comm.adminNotes || '');
    if (comm.status === 'NEW') {
      updateCommunicationStatus(comm.id, 'READ');
    }
  };

  const handleSaveAdminNote = async () => {
    if (!selectedMessage) return;
    setIsSavingNote(true);
    await updateCommunicationStatus(selectedMessage.id, selectedMessage.status, adminNoteInput.trim());
    setSelectedMessage((prev) => (prev ? { ...prev, adminNotes: adminNoteInput.trim() } : null));
    setIsSavingNote(false);
  };

  const handleStatusToggle = async (commId: string, nextStatus: CommunicationStatus) => {
    await updateCommunicationStatus(commId, nextStatus);
    if (selectedMessage?.id === commId) {
      setSelectedMessage((prev) => (prev ? { ...prev, status: nextStatus } : null));
    }
  };

  const handleDelete = async (commId: string) => {
    if (confirm('Bu mesajı kalıcı olarak silmek istediğinizden emin misiniz?')) {
      await deleteCommunication(commId);
      if (selectedMessage?.id === commId) {
        setSelectedMessage(null);
      }
    }
  };

  const handleExportCsv = () => {
    if (!communications || communications.length === 0) return;

    const headers = ['Tarih', 'Durum', 'Ad Soyad', 'E-posta', 'Telefon', 'Konu', 'Mesaj', 'Kaynak', 'Admin Notu'];
    const rows = filteredCommunications.map((c) => [
      new Date(c.createdAt).toLocaleString('tr-TR'),
      c.status === 'NEW' ? 'YENİ' : c.status === 'READ' ? 'OKUNDU' : 'ARŞİV',
      `"${c.name.replace(/"/g, '""')}"`,
      c.email,
      c.phone || '',
      `"${(c.subject || '').replace(/"/g, '""')}"`,
      `"${c.message.replace(/"/g, '""').replace(/\n/g, ' ')}"`,
      c.source || 'contact_form',
      `"${(c.adminNotes || '').replace(/"/g, '""')}"`,
    ]);

    const csvContent = '\uFEFF' + [headers.join(';'), ...rows.map((r) => r.join(';'))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `${activeSite.slug}-iletisim-mesajlari-${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const filteredCommunications = communications.filter((comm) => {
    if (comm.isDeleted) return false;
    const matchesStatus = statusFilter === 'ALL' || comm.status === statusFilter;
    const q = (searchQuery || '').toLowerCase().trim();
    const matchesSearch =
      !q ||
      (comm.name ? comm.name.toLowerCase().includes(q) : false) ||
      (comm.email ? comm.email.toLowerCase().includes(q) : false) ||
      (comm.phone ? comm.phone.toLowerCase().includes(q) : false) ||
      (comm.subject ? comm.subject.toLowerCase().includes(q) : false) ||
      (comm.message ? comm.message.toLowerCase().includes(q) : false);
    return matchesStatus && matchesSearch;
  });

  const getStatusBadge = (status: CommunicationStatus) => {
    switch (status) {
      case 'NEW':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold bg-blue-50 text-blue-700 border border-blue-200">
            <span className="w-1.5 h-1.5 rounded-full bg-blue-600 animate-pulse" />
            Yeni Mesaj
          </span>
        );
      case 'READ':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-slate-100 text-slate-700 border border-slate-200">
            <CheckCircle2 className="w-3 h-3 text-slate-500" />
            Okundu
          </span>
        );
      case 'ARCHIVED':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-amber-50 text-amber-700 border border-amber-200">
            <Archive className="w-3 h-3 text-amber-600" />
            Arşivlendi
          </span>
        );
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto space-y-6 animate-in fade-in">
      {/* Header with Title and Actions */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-teal-600 font-semibold text-xs uppercase tracking-wider">
            <MessageSquare className="w-4 h-4" />
            <span>Medya ve İletişim • Web Sitesi Mesaj Kutusu</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight mt-1">
            Web Sitesi Ziyaretçi Mesajları
          </h1>
          <p className="text-xs sm:text-sm text-slate-500 mt-0.5 max-w-2xl">
            Web sitenizdeki İletişim Formu ve teklif modülleri üzerinden ziyaretçileriniz tarafından gönderilen mesajlar yalnızca buraya düşer. Adiosoft teknik destek ve ek hizmet talepleriniz için <span className="font-semibold text-blue-600">Taleplerim</span> alanını kullanabilirsiniz.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="px-3 py-2 rounded-xl bg-white border border-slate-200 text-xs font-semibold text-slate-700 hover:bg-slate-50 shadow-xs flex items-center gap-1.5 transition"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${isRefreshing ? 'animate-spin text-blue-600' : ''}`} />
            <span className="hidden sm:inline">Yenile</span>
          </button>

          {communications.length > 0 && (
            <button
              type="button"
              onClick={handleExportCsv}
              className="px-3.5 py-2 rounded-xl bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800 shadow-sm flex items-center gap-1.5 transition"
            >
              <Download className="w-3.5 h-3.5" />
              <span>Dışa Aktar (CSV)</span>
            </button>
          )}
        </div>
      </div>

      {/* Metric / Filter Pills Row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div
          onClick={() => setStatusFilter('ALL')}
          className={`p-4 rounded-2xl border cursor-pointer transition ${
            statusFilter === 'ALL'
              ? 'bg-blue-50/70 border-blue-300 ring-2 ring-blue-500/20'
              : 'bg-white border-slate-200 hover:border-slate-300'
          }`}
        >
          <div className="text-xs font-semibold text-slate-500">Toplam Mesaj</div>
          <div className="text-2xl font-black text-slate-900 mt-1">{communications.length}</div>
        </div>

        <div
          onClick={() => setStatusFilter('NEW')}
          className={`p-4 rounded-2xl border cursor-pointer transition ${
            statusFilter === 'NEW'
              ? 'bg-blue-50/70 border-blue-300 ring-2 ring-blue-500/20'
              : 'bg-white border-slate-200 hover:border-slate-300'
          }`}
        >
          <div className="text-xs font-semibold text-blue-600 flex items-center justify-between">
            <span>Yeni / Yanıt Bekleyen</span>
            {unreadCommunicationsCount > 0 && (
              <span className="w-2 h-2 rounded-full bg-blue-600 animate-ping" />
            )}
          </div>
          <div className="text-2xl font-black text-blue-700 mt-1">{unreadCommunicationsCount}</div>
        </div>

        <div
          onClick={() => setStatusFilter('READ')}
          className={`p-4 rounded-2xl border cursor-pointer transition ${
            statusFilter === 'READ'
              ? 'bg-blue-50/70 border-blue-300 ring-2 ring-blue-500/20'
              : 'bg-white border-slate-200 hover:border-slate-300'
          }`}
        >
          <div className="text-xs font-semibold text-slate-500">Okunmuş</div>
          <div className="text-2xl font-black text-slate-900 mt-1">
            {communications.filter((c) => c.status === 'READ').length}
          </div>
        </div>

        <div
          onClick={() => setStatusFilter('ARCHIVED')}
          className={`p-4 rounded-2xl border cursor-pointer transition ${
            statusFilter === 'ARCHIVED'
              ? 'bg-blue-50/70 border-blue-300 ring-2 ring-blue-500/20'
              : 'bg-white border-slate-200 hover:border-slate-300'
          }`}
        >
          <div className="text-xs font-semibold text-slate-500">Arşivlenen</div>
          <div className="text-2xl font-black text-slate-900 mt-1">
            {communications.filter((c) => c.status === 'ARCHIVED').length}
          </div>
        </div>
      </div>

      {/* Search & Filter Bar */}
      <div className="bg-white rounded-2xl border border-slate-200 p-3 flex flex-col sm:flex-row items-center gap-3">
        <div className="relative flex-1 w-full">
          <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="İsim, e-posta, telefon veya mesaj içinde ara..."
            className="w-full pl-9 pr-4 py-2 text-xs sm:text-sm bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-blue-500 focus:bg-white transition"
          />
        </div>

        <div className="flex items-center gap-2 w-full sm:w-auto">
          <span className="text-xs font-semibold text-slate-500 flex items-center gap-1 shrink-0">
            <Filter className="w-3.5 h-3.5" /> Durum:
          </span>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as any)}
            className="text-xs font-semibold bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 outline-none focus:border-blue-500"
          >
            <option value="ALL">Tüm Durumlar ({communications.length})</option>
            <option value="NEW">Yeniler ({unreadCommunicationsCount})</option>
            <option value="READ">Okunmuşlar</option>
            <option value="ARCHIVED">Arşivlenenler</option>
          </select>
        </div>
      </div>

      {/* Main Content: Split List / Detail or Clean Responsive Table */}
      {filteredCommunications.length === 0 ? (
        <div className="bg-white rounded-3xl border border-slate-200 p-12 text-center space-y-4 shadow-xs">
          <div className="w-14 h-14 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center mx-auto">
            <Inbox className="w-7 h-7" />
          </div>
          <div className="max-w-md mx-auto">
            <h3 className="text-base font-bold text-slate-900">
              {searchQuery ? 'Aramanızla eşleşen mesaj bulunamadı' : 'Henüz gelen bir iletişim mesajı yok'}
            </h3>
            <p className="text-xs text-slate-500 mt-1 leading-relaxed">
              {searchQuery
                ? 'Farklı bir anahtar kelime veya filtre seçeneği deneyebilirsiniz.'
                : 'Web sitenizdeki iletişim formunu dolduran ziyaretçilerin tüm mesajları anında bu alana düşecektir.'}
            </p>
          </div>
          {searchQuery && (
            <button
              type="button"
              onClick={() => {
                setSearchQuery('');
                setStatusFilter('ALL');
              }}
              className="px-4 py-2 rounded-xl bg-slate-100 text-xs font-bold text-slate-700 hover:bg-slate-200 transition"
            >
              Filtreleri Temizle
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
          {/* Messages List (Takes full width if no selection on mobile, or 5/7 columns) */}
          <div className={`${selectedMessage ? 'lg:col-span-5' : 'lg:col-span-12'} space-y-3`}>
            {filteredCommunications.map((comm, index) => {
              const isSelected = selectedMessage?.id === comm.id;
              const formattedDate = new Date(comm.createdAt).toLocaleString('tr-TR', {
                day: 'numeric',
                month: 'short',
                hour: '2-digit',
                minute: '2-digit',
              });

              const itemKey = comm.id ? `${comm.id}-${index}` : `comm-${comm.createdAt || 'item'}-${index}`;

              return (
                <div
                  key={itemKey}
                  onClick={() => handleOpenDetail(comm)}
                  className={`bg-white rounded-2xl border p-4 cursor-pointer transition-all duration-150 relative ${
                    isSelected
                      ? 'border-blue-500 ring-2 ring-blue-500/20 shadow-md bg-blue-50/20'
                      : comm.status === 'NEW'
                      ? 'border-blue-200 bg-white hover:border-blue-300 shadow-xs'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-2 min-w-0">
                      {comm.status === 'NEW' && (
                        <span className="w-2.5 h-2.5 rounded-full bg-blue-600 shrink-0" />
                      )}
                      <span className={`text-sm font-bold truncate ${comm.status === 'NEW' ? 'text-slate-950 font-black' : 'text-slate-900'}`}>
                        {comm.name}
                      </span>
                    </div>
                    <span className="text-[11px] font-medium text-slate-400 shrink-0 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {formattedDate}
                    </span>
                  </div>

                  <div className="flex items-center gap-3 text-xs text-slate-500 mt-1.5">
                    <span className="truncate flex items-center gap-1">
                      <Mail className="w-3 h-3 text-slate-400" />
                      {comm.email}
                    </span>
                    {comm.phone && (
                      <span className="hidden sm:flex items-center gap-1 truncate text-slate-500">
                        <Phone className="w-3 h-3 text-slate-400" />
                        {comm.phone}
                      </span>
                    )}
                  </div>

                  <p className="text-xs text-slate-600 mt-2 line-clamp-2 leading-relaxed">
                    {comm.message}
                  </p>

                  <div className="flex items-center justify-between mt-3 pt-2.5 border-t border-slate-100">
                    <div className="flex items-center gap-2">
                      {getStatusBadge(comm.status)}
                      {comm.adminNotes && (
                        <span className="text-[10px] font-semibold bg-slate-100 text-slate-600 px-2 py-0.5 rounded-md">
                          Not Var
                        </span>
                      )}
                    </div>

                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenDetail(comm);
                      }}
                      className="text-xs font-bold text-blue-600 hover:text-blue-700 flex items-center gap-1"
                    >
                      <span>İncele</span>
                      <Eye className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Message Reader / Detail Panel */}
          {selectedMessage && (
            <div className="lg:col-span-7 bg-white rounded-3xl border border-slate-200 p-6 sm:p-7 space-y-6 shadow-sm sticky top-24">
              {/* Header Details */}
              <div className="flex items-start justify-between gap-4 pb-4 border-b border-slate-100">
                <div>
                  <div className="flex items-center gap-2 mb-1.5">
                    {getStatusBadge(selectedMessage.status)}
                    <span className="text-xs text-slate-400">
                      {new Date(selectedMessage.createdAt).toLocaleString('tr-TR', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </span>
                  </div>
                  <h2 className="text-xl font-extrabold text-slate-900">{selectedMessage.name}</h2>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {selectedMessage.subject || 'İletişim Formu Talebi'}
                  </div>
                </div>

                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() =>
                      handleStatusToggle(
                        selectedMessage.id,
                        selectedMessage.status === 'ARCHIVED' ? 'READ' : 'ARCHIVED'
                      )
                    }
                    className="p-2 rounded-xl text-slate-500 hover:text-slate-800 hover:bg-slate-100 transition"
                    title={selectedMessage.status === 'ARCHIVED' ? 'Arşivden Çıkar' : 'Arşive Kaldır'}
                  >
                    <Archive className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(selectedMessage.id)}
                    className="p-2 rounded-xl text-rose-500 hover:text-rose-700 hover:bg-rose-50 transition"
                    title="Mesajı Sil"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setSelectedMessage(null)}
                    className="p-2 rounded-xl text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition"
                  >
                    ✕
                  </button>
                </div>
              </div>

              {/* Quick Contact Action Buttons */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                <a
                  href={`mailto:${selectedMessage.email}?subject=${encodeURIComponent(
                    `Re: ${activeSite.name} — Talebiniz Hakkında`
                  )}`}
                  className="py-2.5 px-3.5 rounded-xl bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200 text-xs font-bold flex items-center justify-center gap-2 transition"
                >
                  <Mail className="w-3.5 h-3.5" />
                  <span>E-posta Yanıtla</span>
                </a>

                {selectedMessage.phone ? (
                  <a
                    href={`tel:${selectedMessage.phone.replace(/[^0-9+]/g, '')}`}
                    className="py-2.5 px-3.5 rounded-xl bg-slate-100 text-slate-800 hover:bg-slate-200 border border-slate-200 text-xs font-bold flex items-center justify-center gap-2 transition"
                  >
                    <Phone className="w-3.5 h-3.5" />
                    <span>Hemen Ara</span>
                  </a>
                ) : (
                  <div className="py-2.5 px-3.5 rounded-xl bg-slate-50 text-slate-400 border border-slate-100 text-xs font-semibold text-center">
                    Telefon Yok
                  </div>
                )}

                {selectedMessage.phone ? (
                  <a
                    href={`https://wa.me/${selectedMessage.phone.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(
                      `Merhaba ${selectedMessage.name}, ${activeSite.name} web sitemiz üzerinden ilettiğiniz mesajınız için teşekkür ederiz.`
                    )}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="py-2.5 px-3.5 rounded-xl bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200 text-xs font-bold flex items-center justify-center gap-2 transition"
                  >
                    <MessageCircle className="w-3.5 h-3.5" />
                    <span>WhatsApp</span>
                  </a>
                ) : (
                  <div className="py-2.5 px-3.5 rounded-xl bg-slate-50 text-slate-400 border border-slate-100 text-xs font-semibold text-center">
                    WhatsApp Yok
                  </div>
                )}
              </div>

              {/* Message Body */}
              <div className="space-y-2">
                <div className="text-xs font-bold text-slate-700 uppercase tracking-wider">Mesaj İçeriği</div>
                <div className="p-4 rounded-2xl bg-slate-50 border border-slate-100 text-xs sm:text-sm text-slate-800 leading-relaxed whitespace-pre-wrap">
                  {selectedMessage.message}
                </div>
              </div>

              {/* Contact Info Card */}
              <div className="bg-slate-50/80 rounded-2xl p-4 border border-slate-100 space-y-2 text-xs">
                <div className="font-bold text-slate-700">İletişim Bilgileri:</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-slate-600">
                  <div>
                    <span className="font-medium text-slate-400">E-posta:</span> {selectedMessage.email}
                  </div>
                  <div>
                    <span className="font-medium text-slate-400">Telefon:</span> {selectedMessage.phone || 'Belirtilmemiş'}
                  </div>
                  <div>
                    <span className="font-medium text-slate-400">Form Kaynağı:</span> {selectedMessage.source || 'İletişim Formu'}
                  </div>
                  <div>
                    <span className="font-medium text-slate-400">Durum:</span>{' '}
                    {selectedMessage.status === 'NEW' ? 'Yeni' : selectedMessage.status === 'READ' ? 'Okundu' : 'Arşivde'}
                  </div>
                </div>
              </div>

              {/* Internal Admin Note Box */}
              <div className="space-y-2 pt-2 border-t border-slate-100">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-bold text-slate-700">İç Notlar (Sadece siz görürsünüz)</label>
                  {selectedMessage.adminNotes && !isSavingNote && (
                    <span className="text-[10px] text-emerald-600 font-semibold">Not kaydedildi</span>
                  )}
                </div>
                <textarea
                  rows={2}
                  value={adminNoteInput}
                  onChange={(e) => setAdminNoteInput(e.target.value)}
                  placeholder="Bu müşteri veya talep hakkında hatırlatıcı bir not yazın..."
                  className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-xs text-slate-800 bg-white outline-none focus:border-blue-500 resize-none"
                />
                <button
                  type="button"
                  onClick={handleSaveAdminNote}
                  disabled={isSavingNote}
                  className="px-4 py-2 rounded-xl bg-slate-900 text-white text-xs font-bold hover:bg-slate-800 transition disabled:opacity-50"
                >
                  {isSavingNote ? 'Kaydediliyor...' : 'Notu Kaydet'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CommunicationsManager;
